import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type Transition from '@ember/routing/transition';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { ReimaginedHomeModel } from 'tio-employee/routes/authenticated/reimagined/home';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type DashboardRouteModel = Resolved<ReturnType<AuthenticatedDashboardRoute['model']>>;

export default class AuthenticatedDashboardRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare partner: PartnerService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @service declare reimaginedData: ReimaginedDataService;

  // check for mentorship form submission re-route without passing
  // as a param that needs to be reset to prevent persistence
  beforeModel(transition: Transition) {
    if (transition.to?.queryParams.formSubmitted) {
      const successMessage = this.intl.t('tuition_assistance.mentoring.submit_success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    }
  }

  async model() {
    const personId = this.sessionContext.user.person.id!;

    // Only load accounts.loans - this is what loan-summary widget needs
    // Other employee/company data is already loaded via /me endpoint in session-context
    const { content: personContent } = (await this.store.request(
      findRecord<PersonModel>('person', personId, {
        include: 'accounts.loans,accounts.postal-address',
        reload: true,
      })
    )) as { content: { data: PersonModel } };
    const person = personContent.data;

    // isTaOnly uses employees.plan which is already loaded via /me endpoint
    const employees = this.sessionContext.user.employees;
    const isTaOnlyCompany = this.partner.isTaOnly;
    const companyKey = employees.map((e) => e.plan?.companyKey);
    const taOnlyPlanId = this.partner.companySettings?.tuitionAssistance?.taOnlyPlanId;
    const isTaOnlyEmployee = taOnlyPlanId ? companyKey.includes(taOnlyPlanId) : false;

    // Get wellness data from parent authenticated route
    const authenticatedModel = this.modelFor('authenticated') as {
      wellnessData: Record<string, unknown> | null;
    };

    // When beta is enabled, fetch reimagined dashboard data alongside legacy data
    if (this.betaExperience.isEnabled) {
      const [
        financialSnapshot,
        dataSourceInfo,
        employerContributions,
        pslfData,
        tasData,
        section127Info,
      ] = await Promise.all([
        this.reimaginedData.fetchFinancialSnapshot(),
        this.reimaginedData.getDataSourceInfo(),
        this.reimaginedData.fetchEmployerContributions(),
        this.reimaginedData.fetchPslfData(),
        this.reimaginedData.fetchTasData(),
        this.reimaginedData.fetchSection127Info(),
      ]);

      const reimagined: ReimaginedHomeModel = {
        financialSnapshot,
        dataSourceInfo,
        employerContributions,
        pslfData,
        tasData,
        section127Info,
      };

      return {
        person,
        isTaOnly: isTaOnlyCompany || isTaOnlyEmployee,
        wellnessData: authenticatedModel.wellnessData,
        reimagined,
      };
    }

    return {
      person,
      isTaOnly: isTaOnlyCompany || isTaOnlyEmployee,
      wellnessData: authenticatedModel.wellnessData,
    };
  }
}
